import { useEffect, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, FolderOpen, Users, BookOpen, UserCog, LogOut, Plus, HardHat, CreditCard,
  ShoppingCart, ClipboardCheck, MessageCircle, TrendingUp, Receipt, CalendarDays, FileText,
  Search, MoreHorizontal, X, Command, Building2, Landmark, Radio, FileCog, PiggyBank, Undo2, Coins, ShoppingBag, Globe, FileSignature, Sun, Moon, Sparkles, Briefcase,
} from "lucide-react";
import { DevistoLogo } from "@/components/DevistoLogo";
import { SubscriptionBanner } from "@/components/SubscriptionBanner";
import { NotificationBell } from "@/components/NotificationBell";
import { GlobalSearch } from "@/components/studio/GlobalSearch";
import { InstallPrompt } from "@/components/InstallPrompt";
import { resetTheme, useTheme } from "@/hooks/useTheme";
import { AssistantDock } from "@/components/studio/assistant/AssistantDock";
import { setAppLang } from "@/i18n/appLang";

const NAV_SCROLL_KEY = "dv-nav-scroll";

const NAV = [
  { group: "Pilotage", to: "/studio", label: "Tableau de bord", icon: LayoutDashboard, testId: "studio-nav-home", end: true },
  { group: "Pilotage", to: "/studio/assistant", label: "Assistant IA", icon: Sparkles, testId: "studio-nav-assistant" },
  { group: "Pilotage", to: "/studio/planning", label: "Planning", icon: CalendarDays, testId: "studio-nav-planning" },
  { group: "Chantier", to: "/studio/dossiers", label: "Dossiers", icon: FolderOpen, testId: "studio-nav-dossiers" },
  { group: "Chantier", to: "/studio/batiments", label: "Bâtiments", icon: Building2, testId: "studio-nav-sites" },
  { group: "Chantier", to: "/studio/interventions", label: "Interventions", icon: ClipboardCheck, testId: "studio-nav-interventions" },
  { group: "Chantier", to: "/studio/materiel", label: "Magasin", icon: ShoppingCart, testId: "studio-nav-shopping" },
  { group: "Chantier", to: "/studio/clients", label: "Clients", icon: Users, testId: "studio-nav-clients" },
  { group: "Chantier", to: "/studio/contrats", label: "Contrats d'entretien", icon: FileSignature, testId: "studio-nav-contracts" },
  { group: "Facturation", to: "/studio/devis", label: "Devis", icon: FileText, testId: "studio-nav-quotes" },
  { group: "Chantier", to: "/studio/demandes", label: "Demandes web", icon: Globe, testId: "studio-nav-webrequests" },
  { group: "Chantier", to: "/studio/messages", label: "Questions clients", icon: MessageCircle, testId: "studio-nav-messages" },
  { group: "Facturation", to: "/studio/factures", label: "Factures", icon: Receipt, testId: "studio-nav-invoices", ownerOnly: true },
  { group: "Facturation", to: "/studio/avoirs", label: "Avoirs", icon: Undo2, testId: "studio-nav-credit-notes", ownerOnly: true },
  { group: "Facturation", to: "/studio/reglements", label: "Règlements", icon: Coins, testId: "studio-nav-payments", ownerOnly: true },
  { group: "Facturation", to: "/studio/achats", label: "Achats", icon: ShoppingBag, testId: "studio-nav-purchases", ownerOnly: true },
  { group: "Facturation", to: "/studio/chiffre-affaires", label: "Chiffre d'affaires", icon: TrendingUp, testId: "studio-nav-revenue", ownerOnly: true },
  { group: "Facturation", to: "/studio/peppol", label: "Facturation électronique", icon: Radio, testId: "studio-nav-peppol", ownerOnly: true, beOnly: true },
  { group: "Facturation", to: "/studio/mon-comptable", label: "Mon comptable", icon: Briefcase, testId: "studio-nav-accountant-hub", ownerOnly: true },
  { group: "Mon entreprise", to: "/studio/bibliotheque", label: "Bibliothèque de prix", icon: BookOpen, testId: "studio-nav-library" },
  { group: "Mon entreprise", to: "/studio/finances", label: "Finances", icon: PiggyBank, testId: "studio-nav-finances", ownerOnly: true },
  { group: "Mon entreprise", to: "/studio/documents", label: "Documents", icon: FileCog, testId: "studio-nav-documents", ownerOnly: true },
  { group: "Mon entreprise", to: "/studio/equipe", label: "Équipe", icon: HardHat, testId: "studio-nav-team", ownerOnly: true },
  { group: "Mon entreprise", to: "/studio/abonnement", label: "Abonnement", icon: CreditCard, testId: "studio-nav-subscription", ownerOnly: true },
  { group: "Mon entreprise", to: "/studio/profil", label: "Profil", icon: UserCog, testId: "studio-nav-profile" },
];

const GROUPS = ["Pilotage", "Chantier", "Facturation", "Mon entreprise", "Comptabilité"];
const BOTTOM = ["/studio", "/studio/dossiers", "/studio/devis"];
const ACCOUNTANT_HOME = "/studio/comptable";
const ACCOUNTANT_NAV = [
  { group: "Comptabilité", to: ACCOUNTANT_HOME, label: "Espace comptable", icon: Briefcase, testId: "studio-nav-accountant", end: true },
];

const QUICK = [
  { label: "Nouveau dossier chantier", hint: "Capture terrain, photos, mesures", icon: FolderOpen, to: "/studio/dossiers?nouveau=1", testId: "quick-action-dossier", tone: "orange" },
  { label: "Nouveau devis", hint: "Client, lignes, envoi", icon: FileText, to: "/studio/devis?nouveau=1", testId: "quick-action-quote", tone: "amber" },
  { label: "Nouvelle intervention", hint: "Dépannage, entretien, rapport signé", icon: ClipboardCheck, to: "/studio/interventions?nouveau=1", testId: "quick-action-intervention", tone: "blue" },
  { label: "Nouveau bâtiment", hint: "Passeport technique et équipements", icon: Building2, to: "/studio/batiments?nouveau=1", testId: "quick-action-site", tone: "navy" },
  { label: "Nouvelle facture", hint: "Acompte, finale ou libre", icon: Receipt, to: "/studio/factures?nouveau=1", testId: "quick-action-invoice", ownerOnly: true, tone: "blue" },
];
const TONE_CLS = { blue: "bg-[#EEF1FC] text-[#3A5CC8]", orange: "bg-orange-50 text-[#F97316]", amber: "bg-amber-50 text-amber-600", navy: "bg-[#EEF1FC] text-[#0F2645]" };

const Sheet = ({ title, onClose, children, testId }) => (
  <div data-testid={testId} className="fixed inset-0 z-[60] lg:hidden" role="dialog">
    <div className="absolute inset-0 bg-[#0F2645]/50 backdrop-blur-sm" onClick={onClose} />
    <div
      className="absolute bottom-0 left-0 right-0 max-h-[80vh] overflow-y-auto rounded-t-2xl border-t border-[#E2E7F7] bg-white pb-8"
      style={{ animation: "devSlideUp .2s ease-out" }}
    >
      <div className="sticky top-0 flex items-center justify-between border-b border-[#E2E7F7] bg-white px-5 py-4">
        <p className="font-heading text-base font-bold text-[#0F2645]">{title}</p>
        <button
          type="button"
          data-testid={`${testId}-close`}
          onClick={onClose}
          className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-[#93A0BC] hover:bg-slate-100"
        >
          <X className="h-4.5 w-4.5" />
        </button>
      </div>
      <div className="px-4 pt-3">{children}</div>
    </div>
  </div>
);

export const StudioLayout = ({ title, subtitle, action, breadcrumb, children, ownerOnly = false }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [name, setName] = useState("");
  const [isOwner, setIsOwner] = useState(null);
  const [memberRole, setMemberRole] = useState(() => localStorage.getItem("devisto_member_role") || "");
  const [country, setCountryState] = useState("BE");
  const currency = localStorage.getItem("devisto_currency") || "EUR";
  const [sheet, setSheet] = useState(null);
  const navRef = useRef(null);
  const accountant = memberRole === "comptable";

  const [theme, setTheme] = useTheme();
  useEffect(() => resetTheme, []);

  useEffect(() => {
    if (accountant && !pathname.startsWith(ACCOUNTANT_HOME)) navigate(ACCOUNTANT_HOME, { replace: true });
  }, [accountant, pathname, navigate]);

  useEffect(() => {
    const el = navRef.current;
    if (!el) return;
    const saved = Number(sessionStorage.getItem(NAV_SCROLL_KEY) || 0);
    if (saved) el.scrollTop = saved;
    const active = el.querySelector('[aria-current="page"]');
    if (active) {
      const r = active.getBoundingClientRect();
      const box = el.getBoundingClientRect();
      if (r.top < box.top || r.bottom > box.bottom) active.scrollIntoView({ block: "nearest" });
    }
    const onScroll = () => sessionStorage.setItem(NAV_SCROLL_KEY, String(el.scrollTop));
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [isOwner]);

  useEffect(() => {
    const token = localStorage.getItem("devisto_token");
    const role = localStorage.getItem("devisto_role");
    if (!token || !["artisan", "pro", "entreprise", "admin"].includes(role)) {
      navigate("/connexion");
      return;
    }
    setName(localStorage.getItem("devisto_name") || "");
  }, [navigate]);

  useEffect(() => {
    setAppLang(localStorage.getItem("devisto_app_lang") || "fr");
    import("@/lib/studioApi").then(({ studio, refreshSession, sessionAge, ensureMediaToken, setCurrency }) => {
      if (sessionAge() > 24 * 3600 * 1000) refreshSession().catch(() => {});
      ensureMediaToken();
      studio.get("/whoami").then(({ data }) => { localStorage.setItem("devisto_app_lang", data.lang || "fr"); setAppLang(data.lang || "fr"); setIsOwner(!!data.is_owner); setMemberRole(data.member_role || ""); localStorage.setItem("devisto_member_role", data.member_role || ""); if (data.currency) setCurrency(data.currency); if (data.country) { setCountryState(data.country); localStorage.setItem("devisto_country", data.country); } }).catch(() => {});
    });
    return () => setAppLang("fr");
  }, []);

  const logout = async () => {
    await import("@/lib/push").then(({ disablePush }) => disablePush()).catch(() => {});
    localStorage.removeItem("devisto_token");
    localStorage.removeItem("devisto_media_token");
    localStorage.removeItem("devisto_member_role");
    localStorage.removeItem("devisto_role");
    localStorage.removeItem("devisto_name");
    navigate("/connexion");
  };

  const visible = accountant ? ACCOUNTANT_NAV : NAV.filter((n) => (isOwner !== false || !n.ownerOnly) && (!n.beOnly || country === "BE"));
  const bottomNav = accountant ? ACCOUNTANT_NAV : BOTTOM.map((to) => visible.find((n) => n.to === to)).filter(Boolean);
  const moreNav = visible.filter((n) => !BOTTOM.includes(n.to) && !accountant);
  const quick = QUICK.filter((q) => isOwner !== false || !q.ownerOnly);
  const blocked = ownerOnly && isOwner === false && !accountant;

  const openSheet = (id) => (e) => {
    e?.preventDefault?.();
    setSheet(id);
  };

  const sheetGo = (to) => {
    setSheet(null);
    navigate(to);
  };

  return (
    <div className="dv-studio font-body min-h-screen bg-[#F3F5FC] pb-28 lg:pb-0 lg:pl-[264px]">
      {!accountant && <GlobalSearch />}
      {!blocked && !accountant && <AssistantDock />}
      <InstallPrompt />

      {/* ---------- Sidebar desktop (navy premium) ---------- */}
      <aside
        className="dv-navy fixed bottom-0 left-0 top-0 hidden w-[264px] flex-col overflow-hidden border-r border-white/[0.06] px-3 py-5 lg:flex"
      >
        <div className="relative flex h-10 items-center justify-between px-2">
          <NavLink to="/studio" className="flex items-center gap-2">
            <DevistoLogo variant="white" className="h-6 w-auto" />
            <span className="rounded border border-white/20 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.16em] text-[#C8D2EC]">
              Studio
            </span>
          </NavLink>
          {!accountant && <NotificationBell testId="notification-bell-desktop" />}
        </div>

        {!accountant && (
        <button
          type="button"
          data-testid="studio-search-desktop"
          onClick={() => window.dispatchEvent(new Event("devisto:open-search"))}
          className="relative mt-5 flex h-10 items-center gap-2.5 rounded-lg border border-white/10 bg-white/[0.05] px-3 text-left text-[13px] transition-colors duration-150 hover:border-white/20 hover:bg-white/[0.09]"
        >
          <Search className="h-4 w-4 shrink-0 text-[#9FB4F2]" />
          <span className="flex-1 text-[#9FB4F2]">Rechercher…</span>
          <span className="inline-flex items-center gap-0.5 rounded border border-white/10 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-[#93A0BC]">
            <Command className="h-2.5 w-2.5" />K
          </span>
        </button>
        )}
        {accountant && (
          <div data-testid="accountant-sidebar-badge" className="mt-5 rounded-lg border border-white/10 bg-white/[0.05] px-3 py-2.5 text-[12px] leading-snug text-[#C8D2EC]">
            <span className="font-semibold text-white">Accès comptable</span> · consultation des ventes, achats et exports
          </div>
        )}

        <nav ref={navRef} data-testid="studio-sidebar-nav" className="relative mt-5 flex-1 overflow-y-auto pr-1 [scrollbar-width:thin] [scrollbar-color:rgba(255,255,255,0.12)_transparent]">
          {GROUPS.map((g) => {
            const items = visible.filter((n) => n.group === g);
            if (!items.length) return null;
            return (
              <div key={g} className="mb-5">
                <p className="px-3 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-[#9FB4F2]/60">{g}</p>
                <div className="space-y-px">
                  {items.map((n) => (
                    <NavLink
                      key={n.to}
                      to={n.to}
                      end={n.end}
                      data-testid={n.testId}
                      className={({ isActive }) =>
                        `group relative flex h-9 items-center gap-2.5 rounded-lg px-3 text-[13px] font-medium transition-colors duration-150 ${
                          isActive
                            ? "dv-rail bg-white/[0.09] text-white"
                            : "text-[#C8D2EC] hover:bg-white/[0.06] hover:text-white"
                        }`
                      }
                    >
                      {({ isActive }) => (
                        <>
                          <n.icon className={`h-4 w-4 shrink-0 ${isActive ? "text-white" : "text-[#9FB4F2] group-hover:text-white"}`} strokeWidth={isActive ? 2.4 : 2} />
                          <span className="truncate">{n.label}</span>
                        </>
                      )}
                    </NavLink>
                  ))}
                </div>
              </div>
            );
          })}
        </nav>

        <div className="relative mt-2 border-t border-white/[0.08] pt-3">
          <div data-testid="sidebar-belgian-chip" className="mb-2 flex items-center gap-2 px-3 py-1.5">
            <Landmark className="h-3.5 w-3.5 shrink-0 text-[#F97316]" />
            <span className="truncate text-[10px] font-semibold uppercase tracking-[0.14em] text-[#9FB4F2]">{country === "BE" ? "100 % belge · Peppol UBL" : `${country} · ${currency}`}</span>
          </div>
          <div className="flex items-center justify-between gap-2 px-3">
            <p className="truncate text-xs font-semibold text-[#C8D2EC]">{name}</p>
            <button
              type="button"
              data-testid="studio-logout"
              onClick={logout}
              className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg px-2 text-xs font-medium text-[#93A0BC] transition-colors hover:bg-white/[0.06] hover:text-white"
            >
              <LogOut className="h-3.5 w-3.5" /> Déconnexion
            </button>
          </div>
        </div>
      </aside>

      {/* ---------- Header mobile ---------- */}
      <header className="sticky top-0 z-40 flex h-14 items-center justify-between gap-2 border-b border-[#E2E7F7] bg-white/95 px-4 backdrop-blur lg:hidden">
        <NavLink to="/studio" className="flex min-w-0 items-center gap-1.5">
          <DevistoLogo className="h-5 w-auto shrink-0" />
          <span className="rounded border border-[#E2E7F7] px-1 py-px text-[9px] font-bold uppercase tracking-[0.16em] text-[#5C6A87]">Studio</span>
        </NavLink>
        <div className="flex shrink-0 items-center gap-1">
          {!accountant && (
          <button
            type="button"
            data-testid="studio-search-mobile"
            onClick={() => window.dispatchEvent(new Event("devisto:open-search"))}
            className="inline-flex h-10 w-10 items-center justify-center rounded-xl text-[#5C6A87] active:scale-95"
          >
            <Search className="h-5 w-5" />
          </button>
          )}
          {!accountant && <NotificationBell />}
          {!accountant && (
          <button
            type="button"
            data-testid="studio-new-dossier-top"
            onClick={() => navigate("/studio/dossiers?nouveau=1")}
            className="inline-flex h-9 items-center gap-1.5 rounded-xl bg-[#3A5CC8] shadow-[0_1px_2px_rgba(15,38,69,0.14)] px-3.5 text-xs font-semibold text-white transition-transform duration-75 active:scale-95"
          >
            <Plus className="h-3.5 w-3.5" /> Dossier
          </button>
          )}
        </div>
      </header>

      {/* ---------- Contenu ---------- */}
      <main className="mx-auto max-w-6xl px-4 pt-5 sm:px-6 lg:px-10 lg:pt-8">
        <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3 border-b border-[#E2E7F7] pb-5">
          <div className="min-w-0">
            {breadcrumb && (
              <p className="mb-1 truncate text-[11px] font-semibold uppercase tracking-[0.08em] text-[#93A0BC]">
                {breadcrumb}
              </p>
            )}
            <h1 className="font-heading text-2xl font-bold tracking-tight text-[#0F2645] sm:text-[28px] sm:leading-tight">{title}</h1>
            {subtitle && <p className="mt-1 max-w-2xl text-sm leading-relaxed text-[#5C6A87]">{subtitle}</p>}
          </div>
          {!blocked && action && <div className="flex shrink-0 items-center gap-2">{action}</div>}
        </div>
        <SubscriptionBanner />
        {blocked ? (
          <div data-testid="owner-only-block" className="mt-7 rounded-2xl border border-amber-200 bg-amber-50 p-5 sm:p-6">
            <p className="font-heading text-base font-bold text-amber-900">Réservé au responsable de la société</p>
            <p className="mt-2 text-sm leading-relaxed text-amber-800">
              La facturation et le chiffre d'affaires sont accessibles uniquement au patron. Vous gardez l'accès aux
              dossiers, aux devis et à vos interventions.
            </p>
            <button
              type="button"
              data-testid="owner-only-back"
              onClick={() => navigate("/studio")}
              className="mt-4 inline-flex min-h-[44px] items-center gap-2 rounded-xl bg-[#3A5CC8] shadow-[0_1px_2px_rgba(15,38,69,0.14)] px-5 text-sm font-semibold text-white transition-colors duration-150 hover:bg-[#2F4CA8]"
            >
              Retour au tableau de bord
            </button>
          </div>
        ) : (
          <div style={{ animation: "devFadeUp .18s ease-out" }}>{children}</div>
        )}
      </main>

      {/* ---------- Bottom nav mobile : 4 + 1 ---------- */}
      <nav className="fixed bottom-0 left-0 right-0 z-40 grid border-t border-white/[0.08] bg-[#0F2645] pb-[env(safe-area-inset-bottom)] lg:hidden" style={{ gridTemplateColumns: `repeat(${accountant ? 2 : 5}, minmax(0, 1fr))` }}>
        {bottomNav.slice(0, 2).map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            data-testid={`${n.testId}-mobile`}
            className={({ isActive }) =>
              `flex min-h-[60px] flex-col items-center justify-center gap-1 whitespace-nowrap px-0.5 text-[10px] font-semibold tracking-tight transition-colors duration-150 ${
                isActive ? "text-white" : "text-[#9FB4F2]"
              }`
            }
          >
            <n.icon className="h-5 w-5" /> {n.label}
          </NavLink>
        ))}

        {!accountant && (
        <button
          type="button"
          data-testid="studio-quick-action-btn"
          onClick={openSheet("quick")}
          className="flex min-h-[60px] flex-col items-center justify-center"
        >
          <span className="inline-flex h-11 w-11 -translate-y-2 items-center justify-center rounded-full bg-[#3A5CC8] text-white ring-4 ring-[#0F2645] transition-transform duration-75 active:scale-95">
            <Plus className="h-5 w-5" />
          </span>
          <span className="-mt-1 text-[10px] font-semibold text-white">Créer</span>
        </button>
        )}

        {bottomNav.slice(2).map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            data-testid={`${n.testId}-mobile`}
            className={({ isActive }) =>
              `flex min-h-[60px] flex-col items-center justify-center gap-1 text-[10px] font-semibold transition-colors duration-150 ${
                isActive ? "text-white" : "text-[#9FB4F2]"
              }`
            }
          >
            <n.icon className="h-5 w-5" /> {n.label}
          </NavLink>
        ))}

        <button
          type="button"
          data-testid="studio-nav-more-mobile"
          onClick={openSheet("more")}
          className="flex min-h-[60px] flex-col items-center justify-center gap-1 text-[10px] font-semibold text-[#93A0BC]"
        >
          <MoreHorizontal className="h-5 w-5" /> Plus
        </button>
      </nav>

      {sheet === "quick" && (
        <Sheet title="Que voulez-vous créer ?" onClose={() => setSheet(null)} testId="quick-action-sheet">
          <div className="space-y-2">
            {quick.map((a) => (
              <button
                key={a.testId}
                type="button"
                data-testid={a.testId}
                onClick={() => sheetGo(a.to)}
                className="flex min-h-[60px] w-full items-center gap-3 rounded-xl border border-[#E2E7F7] bg-white px-4 text-left transition-colors hover:bg-[#F6F8FD] active:scale-[0.99]"
              >
                <span className={`inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${TONE_CLS[a.tone] || TONE_CLS.blue}`}>
                  <a.icon className="h-5 w-5" strokeWidth={2.2} />
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-sm font-bold text-[#0F2645]">{a.label}</span>
                  <span className="block truncate text-xs text-[#5C6A87]">{a.hint}</span>
                </span>
              </button>
            ))}
          </div>
        </Sheet>
      )}

      {sheet === "more" && (
        <Sheet title={accountant ? "Mon compte" : "Tout Devisto Studio"} onClose={() => setSheet(null)} testId="more-sheet">
          <div className="grid grid-cols-2 gap-2">
            {moreNav.map((n) => (
              <button
                key={n.to}
                type="button"
                data-testid={`${n.testId}-mobile`}
                onClick={() => sheetGo(n.to)}
                className="flex min-h-[74px] flex-col items-start gap-2 rounded-xl border border-[#E2E7F7] bg-white p-3.5 text-left hover:bg-[#F6F8FD] active:scale-[0.99]"
              >
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-[#EEF1FC] text-[#3A5CC8]">
                  <n.icon className="h-4.5 w-4.5" strokeWidth={2.2} />
                </span>
                <span className="text-[13px] font-semibold leading-tight text-[#0F2645]">{n.label}</span>
              </button>
            ))}
            {!accountant && (
            <button
              type="button"
              data-testid="studio-nav-chat-mobile"
              onClick={() => {
                setSheet(null);
                window.dispatchEvent(new Event("devisto:toggle-chat"));
              }}
              className="flex min-h-[74px] flex-col items-start gap-2 rounded-xl border border-[#E2E7F7] bg-white p-3.5 text-left hover:bg-[#F6F8FD] active:scale-[0.99]"
            >
              <MessageCircle className="h-4.5 w-4.5 text-[#3A5CC8]" />
              <span className="text-[13px] font-semibold leading-tight text-[#0F2645]">Assistant Devisto</span>
            </button>
            )}
          </div>
          <div className="mt-3 flex items-center justify-between gap-3">
            <button
              type="button"
              data-testid="studio-logout-mobile"
              onClick={logout}
              className="inline-flex min-h-[44px] items-center gap-2 rounded-xl px-2 text-sm font-semibold text-[#5C6A87]"
            >
              <LogOut className="h-4 w-4" /> Déconnexion
            </button>
            <button
              type="button"
              data-testid="theme-toggle-mobile"
              aria-pressed={theme === "dark"}
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              className="inline-flex min-h-[44px] items-center gap-2 rounded-xl border border-[#E2E7F7] bg-white px-4 text-sm font-semibold text-[#0F2645] hover:bg-[#F6F8FD] active:scale-[0.98]"
            >
              {theme === "dark" ? <Sun className="h-4 w-4 text-amber-500" /> : <Moon className="h-4 w-4 text-[#3A5CC8]" />}
              {theme === "dark" ? "Mode clair" : "Mode sombre"}
            </button>
          </div>
        </Sheet>
      )}
    </div>
  );
};
